import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import { fullPolicyNotice, privacyPolicyFor, termsOfUseFor } from "../content/privacyPolicy";

/**
 * The policy and the terms of use below it, without any chrome around them. Two places show it: the
 * `/privacy` route and the consent screen, which cannot navigate (it wraps
 * the router) and so opens this over itself instead.
 */
export function PrivacyPolicyBody() {
  const { t, i18n } = useTranslation();
  const policy = privacyPolicyFor(i18n.language);
  const terms = termsOfUseFor(i18n.language);
  const notice = fullPolicyNotice(i18n.language);

  return (
    <>
      <div className="inline-flex size-11 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400">
        <ShieldCheck className="size-5" aria-hidden />
      </div>
      <h1 className="mt-4 text-2xl font-semibold text-slate-900 dark:text-slate-100">
        {t("privacy.title")}
      </h1>
      <p className="mt-4 text-base leading-relaxed text-slate-600 dark:text-slate-400">
        {t("privacy.body")}
      </p>

      <button
        type="button"
        onClick={() => document.getElementById("terms")?.scrollIntoView({ behavior: "smooth" })}
        className="mt-3 text-xs text-slate-500 underline underline-offset-4 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
      >
        {terms.title}
      </button>

      {notice && (
        <p className="mt-8 rounded-lg border border-dashed border-slate-300 p-3 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
          {notice}
        </p>
      )}

      {/* Full texts, English and Russian only — see content/privacyPolicy.ts */}
      <h2 className="mt-8 text-xl font-semibold text-slate-900 dark:text-slate-100">
        {policy.title}
      </h2>
      <p className="mt-1 text-xs text-slate-500">{policy.updated}</p>
      {policy.intro.map((paragraph) => (
        <p key={paragraph} className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
          {paragraph}
        </p>
      ))}
      {policy.sections.map((section) => (
        <section key={section.heading} className="mt-8">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            {section.heading}
          </h2>
          {section.paragraphs.map((paragraph) => (
            <p
              key={paragraph}
              className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400"
            >
              {paragraph}
            </p>
          ))}
        </section>
      ))}

      <div id="terms" className="mt-12 scroll-mt-4 border-t border-slate-200 pt-8 dark:border-slate-800">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{terms.title}</h2>
        <p className="mt-1 text-xs text-slate-500">{terms.updated}</p>
        {terms.intro.map((paragraph) => (
          <p key={paragraph} className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
            {paragraph}
          </p>
        ))}
        {terms.sections.map((section) => (
          <section key={section.heading} className="mt-8">
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {section.heading}
            </h3>
            {section.paragraphs.map((paragraph) => (
              <p
                key={paragraph}
                className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400"
              >
                {paragraph}
              </p>
            ))}
          </section>
        ))}
      </div>
    </>
  );
}
